import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useSearchParams } from 'react-router-dom';
import { createSelector, UnknownAction } from '@reduxjs/toolkit';

type SerializableType = 'string' | 'number' | 'boolean' | 'object' | 'undefined';
type ParamValue = string | number | boolean | object | null | undefined;

const normalise = (data: string | null, type: SerializableType = 'object'): any => {
  if (data === null) return null;
  try {
    switch (type) {
      case 'string':
        return data;
      case 'number':
        return Number(data);
      case 'boolean':
        return data === 'true';
      case 'object':
      default:
        return JSON.parse(data);
    }
  } catch (error) {
    console.error(`searchParams exception (normalise): ${error instanceof Error ? error.message : 'Unknown error'}`);
    return null;
  }
};

const get = (params: URLSearchParams, key: string, type: SerializableType = 'object') => {
  const data = params.get(key);
  return normalise(data, type);
};

const formatForUrl = (value: ParamValue, type: SerializableType): string | undefined => {
  try {
    // signal to remove key
    if (value === undefined || value === null) return undefined;

    // drop empty object or array
    if (type === 'object') {
      if (typeof value === 'object' && Object.keys(value as any).length === 0) {
        return undefined;
      }
      return JSON.stringify(value);
    }

    if (type === 'string') return String(value);
    return JSON.stringify(value);
  } catch (error) {
    console.error('searchParams exception (formatForUrl):', error);
    return undefined;
  }
};

// write many params but skip undefined values and remove keys
const setManyParams = (
  setParams: ReturnType<typeof useSearchParams>[1],
  current: URLSearchParams,
  updates: Record<string, string | undefined>,
) => {
  const prev = current.toString();
  const next = new URLSearchParams(prev);
  for (const [k, v] of Object.entries(updates)) {
    if (v === undefined) next.delete(k);
    else next.set(k, v);
  }
  const nextStr = next.toString();
  if (nextStr === prev) return;
  setParams(next);
};

export const useSearch = (key: string, initialValue: any = undefined, type: SerializableType = 'object') => {
  const [params, setParams] = useSearchParams();
  const [value, setValue] = useState(() => {
    const decoded = get(params, key, type);
    return decoded ?? initialValue;
  });

  useEffect(() => {
    const encoded = formatForUrl(value, type);
    setManyParams(setParams, params, { [key]: encoded });
  }, [key, value, type, params, setParams]);

  return [value, setValue] as const;
};

/**
 * Custom hook for synchronizing a Redux state with URL search parameters.
 * @param key The key of the search parameter.
 * @param selector A function to select the relevant state from the Redux store.
 * @param updater A function to update the Redux state.
 * @param initialValue The initial value to use if the search parameter is not set.
 * @param type The type of the search parameter (string, number, boolean, object, or undefined).
 * @returns An array containing the current value and a function to update it.
 * This hook initializes the Redux state with the value from the URL search parameters on mount, and updates the URL whenever the Redux state changes.
 * It also provides a function to set the value in the Redux state, which will automatically update the URL search parameters.
 */
export const useReduxSearch = (
  key: string,
  selector: (state: any) => any,
  updater: any,
  initialValue: any = undefined,
  type: SerializableType = 'object',
  validate?: (value: any) => boolean,
) => {
  const [params, setParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const reduxValue = useAppSelector(selector);

  const [hydrated, setHydrated] = useState(false);

  // Hydrate Redux from URL before paint
  useLayoutEffect(() => {
    const raw = get(params, key, type);
    const urlValue = raw === null ? initialValue : raw;
    const valid = validate ? validate(urlValue) : true;
    dispatch(updater(valid ? urlValue : initialValue));
    setHydrated(true);
  }, []);

  // After hydration, sync Redux -> URL
  useEffect(() => {
    if (!hydrated) return;
    const encoded = formatForUrl(reduxValue, type);
    setManyParams(setParams, params, { [key]: encoded });
  }, [hydrated, reduxValue, key, type, params, setParams]);

  const setValue = (value: any) => {
    try {
      const valid = validate ? validate(value) : true;
      dispatch(updater(valid ? value : initialValue));
    } catch (error) {
      console.error('searchParams exception (setValue):', error);
    }
  };
  return [reduxValue, setValue, hydrated] as const;
};

export interface UrlParamSpec {
  key: string;
  initialValue?: string | number | boolean | object | undefined;
  type?: SerializableType;
  validate?: (value: any) => boolean;
}

export interface ReduxSearchOption extends UrlParamSpec {
  selector: (state: any) => any;
  updater?: (values: any) => UnknownAction;
}

export const useReduxSearchMulti = (options: ReduxSearchOption[], updateFunction: (values: any) => UnknownAction) => {
  const [params, setParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const [hydrated, setHydrated] = useState(false);

  // Snapshot of the URL on first mount
  const initialUrlSnapshot = useRef<string>(params.toString());
  const initialUrlSyncRef = useRef(false);

  const combinedSelector = useMemo(() => {
    const inputSelectors = options.map((opt) => opt.selector);
    const keys = options.map((opt) => opt.key);

    return createSelector(inputSelectors, (...values: any[]) => {
      const out: Record<string, any> = {};
      for (let i = 0; i < values.length; i++) {
        out[keys[i]] = values[i];
      }
      return out;
    });
  }, [...options.map((opt) => opt.selector), ...options.map((opt) => opt.key)]);

  const reduxValues = useAppSelector(combinedSelector);

  // Hydrate Redux from URL before first render
  useLayoutEffect(() => {
    const initialValues: Record<string, any> = {};
    for (const { key, initialValue, type = 'object', validate } of options) {
      const raw = get(params, key, type);
      const urlValue = raw === null ? initialValue : raw;
      const valid = validate ? validate(urlValue) : true;
      initialValues[key] = valid ? urlValue : initialValue;
    }
    dispatch(updateFunction(initialValues));
    setHydrated(true);
  }, []);

  // After hydration: Sync Redux -> URL
  useEffect(() => {
    if (!hydrated) return;

    const current = new URLSearchParams(params.toString());
    const initial = new URLSearchParams(initialUrlSnapshot.current);

    // Only write keys that were missing on init load (preventing URL flicker)
    if (!initialUrlSyncRef.current) {
      initialUrlSyncRef.current = true;
      const updates: Record<string, string | undefined> = {};
      for (const opt of options) {
        const hadKeyInitially = initial.has(opt.key);
        if (hadKeyInitially) continue;
        updates[opt.key] = formatForUrl(reduxValues[opt.key], opt.type || 'object');
      }
      if (Object.keys(updates).length) setManyParams(setParams, current, updates);
      return;
    }

    // Subsequent syncs: normal behavior
    const updates: Record<string, string | undefined> = {};
    for (const opt of options) {
      updates[opt.key] = formatForUrl(reduxValues[opt.key], opt.type || 'object');
    }
    setManyParams(setParams, current, updates);
  }, [hydrated, options, reduxValues, params, setParams]);

  // Setter to update all values in Redux, with validation
  const setValues = (values: Record<string, any>) => {
    try {
      const validated: Record<string, any> = {};
      options.forEach((opt) => {
        const value = values[opt.key];
        const valid = opt.validate ? opt.validate(value) : true;
        validated[opt.key] = valid ? value : opt.initialValue;
      });
      dispatch(updateFunction(validated));
    } catch (error) {
      console.error('searchParams exception (setValues):', error);
    }
  };

  return [reduxValues, setValues, hydrated] as const;
};

// Custom hook for extracting url search param values
export const useAllSearchParams = () => {
  const [searchParams] = useSearchParams();
  return Object.fromEntries(searchParams.entries());
};

// Currently unused - possibly implement later
export const readParamsFromUrl = (specs: UrlParamSpec[], sp: URLSearchParams): Record<string, any> => {
  const out: Record<string, any> = {};
  for (const { key, initialValue, type = 'object', validate } of specs) {
    const raw = get(sp, key, type);
    const urlValue = raw === null ? initialValue : raw;
    const isValid = validate ? validate(urlValue) : true;
    out[key] = isValid ? urlValue : initialValue;
  }
  return out;
};

export const useUrlParamsReadOnly = (specs: UrlParamSpec[]) => {
  const [sp] = useSearchParams();
  return useMemo(() => readParamsFromUrl(specs, sp), [specs, sp]);
};
